import React, { useEffect, useState, useCallback } from 'react';
import { rolesAPI } from '../services/api';
import { Table, Btn, Modal, Card, useToast, Input, Spinner } from '../components/UI';

// ── Helpers ───────────────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--text3)', whiteSpace: 'nowrap' }}>
        {children}
      </span>
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }}/>
    </div>
  );
}

function RoleIcon({ name, size = 36 }) {
  const colors = ['#2d6a4f', '#1a5276', '#7c3aed', '#b5862a', '#c0392b', '#0f6e56'];
  const color  = colors[(name?.charCodeAt(0) ?? 0) % colors.length];
  return (
    <div style={{
      width: size, height: size, borderRadius: 8,
      background: color + '1a', border: `1.5px solid ${color}44`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.4, fontWeight: 700, color, flexShrink: 0,
    }}>
      {name?.charAt(0).toUpperCase() ?? '?'}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Roles() {
  const { show, ToastEl } = useToast();
  const [roles, setRoles]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editModal, setEditModal] = useState(null);   // null | { id?, name, description }
  const [deleteModal, setDeleteModal] = useState(null); // null | role row

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await rolesAPI.list();
      setRoles(data.data);
    } catch { show('Failed to load roles', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setEditModal({ name: '', description: '' });
  }

  function openEdit(role, e) {
    e?.stopPropagation();
    setEditModal({ id: role.id, name: role.name, description: role.description || '' });
  }

  function openDelete(role, e) {
    e?.stopPropagation();
    setDeleteModal(role);
  }

  const columns = [
    { key: 'name', label: 'Role', render: (v, row) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <RoleIcon name={v} size={34}/>
        <div>
          <div style={{ fontWeight: 600 }}>{v}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>
            {row.description || <em style={{ opacity: .6 }}>No description</em>}
          </div>
        </div>
      </div>
    )},
    { key: 'user_count', label: 'Members', render: v => (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontFamily: 'var(--mono)', fontSize: 13,
        color: v > 0 ? 'var(--accent2)' : 'var(--text3)',
        fontWeight: v > 0 ? 600 : 400,
      }}>
        {v}
        {v > 0 && <span style={{ fontSize: 10, opacity: .7 }}>users</span>}
      </span>
    )},
    { key: 'created_at', label: 'Created', render: v =>
      new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    },
    { key: 'actions', label: '', render: (_, row) => (
      <div style={{ display: 'flex', gap: 6 }}>
        <Btn size="sm" variant="outline" onClick={e => openEdit(row, e)}>Edit</Btn>
        <Btn size="sm" variant="ghost"   onClick={e => openDelete(row, e)}
          style={{ color: row.user_count > 0 ? 'var(--text3)' : 'var(--red)', borderColor: 'var(--border)' }}>
          Delete
        </Btn>
      </div>
    )},
  ];

  return (
    <div style={{ padding: '28px 32px', animation: 'fadeIn .35s ease' }}>
      {ToastEl}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: 26, fontWeight: 700 }}>Roles</h1>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 4 }}>Manage roles used to control content and announcement access.</p>
        </div>
        <Btn variant="primary" onClick={openCreate}>+ Add role</Btn>
      </div>

      <Card>
        <Table columns={columns} data={roles} loading={loading} emptyMsg="No roles found."/>
      </Card>

      {/* ── Create / Edit Modal ── */}
      {editModal && (
        <RoleFormModal
          initial={editModal}
          onClose={() => setEditModal(null)}
          onSuccess={msg => { setEditModal(null); load(); show(msg); }}
          onError={msg => show(msg, 'error')}
        />
      )}

      {/* ── Delete Confirmation Modal ── */}
      {deleteModal && (
        <DeleteModal
          role={deleteModal}
          onClose={() => setDeleteModal(null)}
          onSuccess={() => { setDeleteModal(null); load(); show('Role deleted.'); }}
          onError={msg => show(msg, 'error')}
        />
      )}
    </div>
  );
}

// ── Create / Edit Form Modal ──────────────────────────────────────────────────
function RoleFormModal({ initial, onClose, onSuccess, onError }) {
  const isEdit = Boolean(initial.id);
  const [form, setForm]     = useState({ name: initial.name, description: initial.description });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = 'Role name is required';
    else if (form.name.trim().length > 50) e.name = 'Name must be 50 characters or fewer';
    return e;
  }

  async function submit() {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      const body = { name: form.name.trim(), description: form.description.trim() || null };
      if (isEdit) {
        await rolesAPI.update(initial.id, body);
        onSuccess('Role updated.');
      } else {
        await rolesAPI.create(body);
        onSuccess('Role created.');
      }
    } catch (err) {
      onError(err.response?.data?.message || (isEdit ? 'Failed to update role' : 'Failed to create role'));
    } finally { setSaving(false); }
  }

  function field(key, value) {
    setForm(f => ({ ...f, [key]: value }));
    if (errors[key]) setErrors(e => ({ ...e, [key]: '' }));
  }

  return (
    <Modal title={isEdit ? `Edit role — ${initial.name}` : 'Add new role'} onClose={onClose} width={440}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <SectionLabel>Role details</SectionLabel>
        <Input
          label="Role name *"
          placeholder="e.g. Silsila"
          value={form.name}
          onChange={e => field('name', e.target.value)}
          error={errors.name}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)' }}>Description</label>
          <textarea
            placeholder="Brief description of this role…"
            value={form.description}
            onChange={e => field('description', e.target.value)}
            rows={3}
            style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--r)', padding: '8px 12px', fontSize: 13,
              color: 'var(--text)', outline: 'none', width: '100%',
              resize: 'vertical', fontFamily: 'var(--sans)',
            }}
          />
        </div>
        <div style={{
          background: 'var(--accent-light)', border: '1px solid #b7e4c7',
          borderRadius: 'var(--r)', padding: '10px 14px',
          fontSize: 12, color: 'var(--accent2)', lineHeight: 1.6,
        }}>
          The role name is used to control access to content and announcements. It also determines the FCM push notification topic subscribers receive.
        </div>
        <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
          <Btn onClick={submit} disabled={saving}>
            {saving ? <Spinner size={14} color="#fff"/> : (isEdit ? 'Save changes' : 'Create role')}
          </Btn>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        </div>
      </div>
    </Modal>
  );
}

// ── Delete Confirmation Modal ─────────────────────────────────────────────────
function DeleteModal({ role, onClose, onSuccess, onError }) {
  const [deleting, setDeleting] = useState(false);
  const blocked = role.user_count > 0;

  async function confirm() {
    setDeleting(true);
    try {
      await rolesAPI.delete(role.id);
      onSuccess();
    } catch (err) {
      onError(err.response?.data?.message || 'Failed to delete role');
      onClose();
    } finally { setDeleting(false); }
  }

  return (
    <Modal title="Delete role" onClose={onClose} width={420}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <RoleIcon name={role.name} size={44}/>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{role.name}</div>
            {role.description && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{role.description}</div>}
          </div>
        </div>

        {blocked ? (
          <div style={{
            background: 'var(--red-bg, #fdf0ee)', border: '1px solid #f5c6c2',
            borderRadius: 'var(--r)', padding: '12px 14px',
            fontSize: 13, color: 'var(--red)', lineHeight: 1.6,
          }}>
            <strong>Cannot delete.</strong> {role.user_count} user{role.user_count !== 1 ? 's are' : ' is'} assigned to this role.
            Remove them from the role first, then delete it.
          </div>
        ) : (
          <div style={{
            background: 'var(--red-bg, #fdf0ee)', border: '1px solid #f5c6c2',
            borderRadius: 'var(--r)', padding: '12px 14px',
            fontSize: 13, color: 'var(--red)', lineHeight: 1.6,
          }}>
            This will permanently delete the <strong>{role.name}</strong> role. Any content or announcements restricted to this role will lose the role assignment. This action cannot be undone.
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          {!blocked && (
            <Btn variant="danger" onClick={confirm} disabled={deleting}>
              {deleting ? <Spinner size={14} color="#fff"/> : 'Delete role'}
            </Btn>
          )}
          <Btn variant="ghost" onClick={onClose}>
            {blocked ? 'Close' : 'Cancel'}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
